import { LoadingHUD } from './LoadingHUD';
import { BoundaryFeedback } from './BoundaryFeedback';
import { DebugHUD } from './DebugHUD';
import { Crosshair } from './Crosshair';
import { SourcesPanel } from './SourcesPanel';
import { EraToggle } from './EraToggle';
import { BoardingOverlay } from './BoardingOverlay';
import { ReturnToStreetcarButton } from './ReturnToStreetcarButton';
import { TourMenu } from './TourMenu';
import { CaptionOverlay } from './CaptionOverlay';
import { Minimap } from './Minimap';
import { MuteButton } from './MuteButton';
import { NarrationPlayer } from '../voiceover/NarrationPlayer';
import { FpsGuardToast } from '../perf/FpsGuardToast';
import { KeyboardHelp } from './KeyboardHelp';
import { TitleCards } from './TitleCards';
import { RouteMap } from './RouteMap';

/**
 * Composes every DOM overlay that sits on top of the R3F <Canvas>.
 *
 * Z-index order (highest wins):
 *   200 — DebugHUD (dev only, must win over everything when enabled)
 *   150 — BoardingOverlay (Plan 05-01 / NAV-02 — opening card; visible only
 *         when navState === 'idle'; above LoadingHUD so the Board button is
 *         always clickable once assets finish loading)
 *   100 — LoadingHUD (covers everything until manifest + assets are ready —
 *         correctly blocks SourcesPanel interaction during initial load)
 *    50 — SourcesPanel (Plan 04-03 / PARA-03 — right-side period-quiet serif)
 *    45 — TourMenu (Plan 05-03 / NAV-05 — top-left guided-tour picker;
 *         visible only when navState === 'freeWalk')
 *    40 — ReturnToStreetcarButton (Plan 05-02 / NAV-06 — top-right persistent
 *         REBOARD escape; visible only when navState ∈ {freeWalk, guidedTour})
 *    40 — MuteButton (Plan 07-06 / UI-02 5th control — top-right sibling of
 *         ReturnToStreetcarButton; visible only when a tour is active)
 *    40 — EraToggle (Plan 04-04 / ERA-01 — bottom-center period-quiet serif;
 *         hidden outside vistaZones; disabled during eraTransitioning)
 *    30 — CaptionOverlay (Plan 05-03 / NAV-05 — bottom-center caption during
 *         guided tour; visible only when useTourStore.activeCaption is set)
 *    30 — Minimap (Plan 05-04 / UI-03 — bottom-right SVG; visible only when
 *         navState ∈ {freeWalk, guidedTour}. pointer-events:none so it never
 *         blocks clicks; shares z-index with CaptionOverlay because they
 *         occupy disjoint screen regions — bottom-right vs bottom-center)
 *   180 — FpsGuardToast (Plan 07-05 / PERF-01 runtime surface — visible only
 *         when useFpsStore.degraded && !dismissed; bottom-center period chrome
 *         with a /read escape-hatch link)
 *    10 — BoundaryFeedback (warm vignette — below panel, above crosshair)
 *     5 — Crosshair (bottom of UI stack, should never overlap chrome)
 *
 * App.tsx mounts <UIShell /> as a sibling of <Renderer /> so these DOM
 * elements render outside the Canvas element but on top via fixed positioning.
 */
export function UIShell() {
  return (
    <>
      {/* Headless narration <audio>+<track> (Plan 07-02). Positioned first since
          it renders nothing until a tour is active; does not participate in the
          visible z-index stack. */}
      <NarrationPlayer />
      <BoardingOverlay />
      {/* Plan 08 — full-screen title cards at nav transitions + persistent
          bottom-left location label. Cards z-index 160 (above LoadingHUD=100,
          above BoardingOverlay=150, below DebugHUD=200); location label at
          z-index 20 (below most UI, above BoundaryFeedback=10). */}
      <TitleCards />
      {/* Phase 8 continuation — illustrated period-style route-map inset
          showing the streetcar's position between Birmingham and Edgewood
          Lake during boarding / streetcarRiding / arrivalCinematic. Hidden
          otherwise so it doesn't compete with Minimap in freeWalk. z-index
          35 sits between BoundaryFeedback=10 and the persistent chrome. */}
      <RouteMap />
      {/* RideVignette removed 2026-04-24 in the chase-cam pass — the
          vignette's purpose was to reinforce the "looking through a
          streetcar window" read, which no longer applies now that the
          camera sits BEHIND the car (third-person chase) rather than
          INSIDE it. BoundaryFeedback still owns the transient RED
          warning vignette at z-index 10. */}
      <LoadingHUD />
      <SourcesPanel />
      <TourMenu />
      <ReturnToStreetcarButton />
      <MuteButton />
      <EraToggle />
      <CaptionOverlay />
      <Minimap />
      <FpsGuardToast />
      <BoundaryFeedback />
      <Crosshair />
      {/* Global keybinding dispatcher + ? help overlay (Plan 07-03 / FALL-04 +
          UI-05). Mounted here (sibling of <Canvas>) so its window keydown
          listener registers once on app mount and its role=dialog overlay
          sits above everything non-debug. */}
      <KeyboardHelp />
      <DebugHUD />
    </>
  );
}
